import { Close, ErrorOutline, CheckCircleOutline } from "@mui/icons-material";
import React, { createContext, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export const Context = createContext();

export const ContextProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [notification, setNotification] = useState(null);
  const notificationShownRef = useRef(false);

  useEffect(() => {
    const isDarkMode = localStorage.getItem("darkMode") === "true";
    setDarkMode(isDarkMode);
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  const showError = (message) => {
    const text = message || "";
    if (
      text.includes("timeout") ||
      text.includes("Network Error") ||
      text.includes("15000ms") ||
      notificationShownRef.current
    ) {
      return;
    }
    notificationShownRef.current = true;
    setNotification({
      type: "error",
      message,
    });
  };

  const showSuccess = (message) => {
    if (notificationShownRef.current) {
      return;
    }
    notificationShownRef.current = true;
    setNotification({
      type: "success",
      message,
    });
  };

  useEffect(() => {
    if (!notification) return;

    const timer = setTimeout(() => {
      setNotification(null);
      notificationShownRef.current = false;
    }, 4000);

    return () => clearTimeout(timer);
  }, [notification]);

  const closeNotification = () => {
    setNotification(null);
    notificationShownRef.current = false;
  };

  return (
    <Context.Provider
      value={{
        darkMode,
        setDarkMode,
        showError,
        showSuccess,
      }}
    >
      {children}

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={closeNotification}
        />
      )}
    </Context.Provider>
  );
};

const Notification = ({ type, message, onClose }) => {
  const { t } = useTranslation();

  const isSuccess = type === "success";

  return (
    <div
      className={`fixed top-5 right-5 bg-white dark:bg-dark-box 
                  p-4 rounded shadow-lg z-[9999] animate-slide-in-right
                  border-l-4 min-w-[260px] max-w-[360px]
                  ${isSuccess
          ? "border-green text-green"
          : "border-red text-red"
        }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2">
          {isSuccess ? (
            <CheckCircleOutline />
          ) : (
            <ErrorOutline />
          )}

          <p className="text-sm dark:text-dark-text">
            {message || (isSuccess ? t("success") : t("not_success"))}
          </p>
        </div>

        <button type="button" onClick={onClose}>
          <Close fontSize="small" />
        </button>
      </div>
    </div>
  );
};